import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search,
  UserPlus,
  Download,
  Clock,
  Mail,
  RefreshCw,
} from 'lucide-react';
import { userService } from '../services/adminServices';
import UserActionsDropdown from '../components/UserActionsDropdown';
import WaitlistByCityPanel from '../components/users/WaitlistByCityPanel';

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [activeTab, setActiveTab] = useState("all");

  // Stats state
  const [stats, setStats] = useState({
    totalWaitlist: 0,
    approvedToday: 0,
    pendingInvites: 0,
    averageWaitTime: 0
  });

  const fetchUsers = useCallback(
    async (params = {}) => {
      // Only fetch users if the 'all' tab is active
      if (activeTab !== 'all') {
        setLoading(false); // Ensure loading stops if tab switched during fetch
        return;
      }
      try {
        setLoading(true);
        setError(null); // Clear previous errors
        const data = await userService.getUsers({
          page: pagination.currentPage, // Use current page from state
          ...params, // Allow overriding page/other params
          status: statusFilter !== 'all' ? statusFilter : undefined,
          sortBy: sortOrder === 'newest' ? 'createdAt' : '-createdAt', // Corrected sort logic
          sortOrder: sortOrder === 'newest' ? -1 : 1, // Pass sortOrder if needed by API
        });
        setUsers(data.users);
        setPagination({
          currentPage: data.currentPage,
          totalPages: data.totalPages,
          total: data.total,
        });

        // Fetch stats (consider if stats should refresh less often or only on 'all' tab)
        const statsData = await userService.getUserStats();
        setStats(statsData);
      } catch (err) {
        console.error('[UsersPage] Fetch Users Error:', err);
        setError(err.message || 'Failed to load users');
        setUsers([]); // Clear users on error
        setPagination({ currentPage: 1, totalPages: 1, total: 0 }); // Reset pagination
      } finally {
        setLoading(false);
      }
    },
    [activeTab, statusFilter, sortOrder, pagination.currentPage], // Add pagination.currentPage dependency
  );

  const handleRefresh = () => {
    console.log('[UsersPage] Manually refreshing users list');
    fetchUsers({ page: pagination.currentPage, searchTerm });
  };

  useEffect(() => {
    if (activeTab === 'all') {
      fetchUsers({ page: 1 }); // Fetch page 1 when filters/sort change or tab becomes active
    } else {
      // Clear users list when switching away from 'all' tab
      setUsers([]);
      setPagination({ currentPage: 1, totalPages: 1, total: 0 });
      setSelectedUsers([]); // Clear selection
    }
  }, [activeTab, statusFilter, sortOrder, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'all') {
      // Optional: Add debounce here if desired
      const timer = setTimeout(() => {
        if (searchTerm.length >= 2 || searchTerm.length === 0) {
          fetchUsers({ searchTerm: searchTerm, page: 1 });
        }
      }, 300); // 300ms debounce
      return () => clearTimeout(timer);
    }
  }, [searchTerm, activeTab, fetchUsers]);

  // Handler functions

  const handlePageChange = (newPage) => {
    if (activeTab === 'all') {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
      fetchUsers({ page: newPage, searchTerm }); // Fetch new page with current search term
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = (e) => {
    setSelectedUsers(e.target.checked ? users.map(user => user._id) : []);
  };
;

const handleActivateSelected = async () => {
  if (!window.confirm(`Activate ${selectedUsers.length} selected users?`))
    return;
  try {
    setLoading(true); // Show loading indicator during activation
    await userService.activateBatch(selectedUsers);
    await fetchUsers({ page: pagination.currentPage, searchTerm }); // Refresh current view
    setSelectedUsers([]); // Clear selection
  } catch (err) {
    console.error('[UsersPage] Activate Batch Error:', err);
    setError(err.message || 'Failed to activate users');
  } finally {
    setLoading(false);
  }
};


const handleActivateUser = async (userId) => {
  if (!window.confirm(`Activate this user?`)) return;
  try {
    setLoading(true);
    await userService.activateUser(userId);
    await fetchUsers({ page: pagination.currentPage, searchTerm }); // Refresh current view
  } catch (err) {
    console.error('[UsersPage] Activate User Error:', err);
    setError(err.message || 'Failed to activate user');
  } finally {
    setLoading(false);
  }
};


  const formatDate = (dateString) => {
    if (!dateString) return 'Not Set';
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date) 
      ? date.toLocaleDateString()
      : 'Not Set';
  };

  const StatusBadge = ({ status }) => {
    const getStatusStyles = (status) => {
      switch (status) {
        case 'active':
          return 'bg-green-100 text-green-800';
        case 'waitlist':
          return 'bg-yellow-100 text-yellow-800';
        case 'suspended':
          return 'bg-red-100 text-red-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
  
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusStyles(status)}`}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
      </span>
    );
  };

  const ErrorDisplay = () => {
    if (!error) return null;
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <span className="block sm:inline">{error}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <ErrorDisplay />

      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">Manage users and waitlist approvals</p>
        </div>
        {activeTab === 'all' && (
          <div className="flex space-x-3">
            <button
              onClick={handleRefresh}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
              disabled={loading}
              title="Refresh List"
            >
              <RefreshCw
                className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`}
              />
            </button>
            <button
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
              title="Download Users (Not Implemented)"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleActivateSelected}
              disabled={selectedUsers.length === 0 || loading}
            >
              <UserPlus className="h-5 w-5" />
              <span>Activate ({selectedUsers.length})</span>
            </button>
          </div>
        )}
      </div>

            {/* Tab Navigation */}
            <div className="flex border-b mb-4">
        <button
          className={`px-4 py-2 font-semibold ${
            activeTab === 'all'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('all')}
        >
          All Users
        </button>
        <button
          className={`ml-4 px-4 py-2 font-semibold ${
            activeTab === 'waitlistByCity'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => setActiveTab('waitlistByCity')}
        >
          Waitlist by City
        </button>
      </div>

      {/* Loading Indicator (Shows during fetch) */}
      {loading && (
        <div className="text-center py-10">
          <RefreshCw className="h-6 w-6 animate-spin inline-block text-blue-500" />
          <span className="ml-2">Loading...</span>
        </div>
      )}

            {/* Tab Content */}
            {!loading && activeTab === 'all' && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Users Card */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <UserPlus className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Users
                  </p>
                  <h3 className="text-xl font-bold text-gray-900">
                    {pagination.total.toLocaleString()}
                  </h3>
                </div>
              </div>
            </div>
            {/* Waitlist Card */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-50 rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    In Waitlist
                  </p>
                  <h3 className="text-xl font-bold text-gray-900">
                    {stats.totalWaitlist.toLocaleString()}
                  </h3>
                </div>
              </div>
            </div>
            {/* Approved Today Card */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-50 rounded-lg">
                  <UserPlus className="h-5 w-5 text-green-600" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Approved Today
                  </p>
                  <h3 className="text-xl font-bold text-gray-900">
                    {stats.approvedToday.toLocaleString()}
                  </h3>
                </div>
              </div>
            </div>
            {/* Pending Invites Card */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Mail className="h-5 w-5 text-purple-600" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Wait (Days)
                  </p>
                  <h3 className="text-xl font-bold text-gray-900">
                    {stats.avgWaitTime || 'N/A'}
                  </h3>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Filters and Search */}
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0 md:space-x-4">
            <div className="w-full md:flex-1 md:max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by email or username..."
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, currentPage: 1 })); // Reset page on filter change
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="waitlist">Waitlist</option>
                <option value="suspended">Suspended</option> {/* If applicable */}
              </select>

              <select
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value);
                  setPagination((prev) => ({ ...prev, currentPage: 1 })); // Reset page on sort change
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={
                        users.length > 0 &&
                        selectedUsers.length === users.length
                      }
                      onChange={handleSelectAll}
                      indeterminate={
                        selectedUsers.length > 0 &&
                        selectedUsers.length < users.length
                      }
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Home City
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Join Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    GoGets
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-10 text-center text-gray-500"
                    >
                      No users found matching your criteria.
                    </td>
                  </tr>
                )}
                {users.map((user) => (
                  <tr
                    key={user._id}
                    className={`hover:bg-gray-50 ${
                      selectedUsers.includes(user._id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => handleUserSelect(user._id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {/* Placeholder for profile photo */}
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={
                              user.profilePhotoUrl ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                user.name || user.username,
                              )}&background=random`
                            }
                            alt=""
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {user.name || user.username}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={user.activationStatus} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.homeCity || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.lastActive)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.totalGoGets || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <UserActionsDropdown
                        user={user}
                        onActivate={handleActivateUser}
                        // Add other actions like suspend, delete, view profile etc.
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages} (
                {pagination.total.toLocaleString()} results)
              </div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1 || loading}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {/* Consider adding dynamic page numbers if many pages */}
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  {pagination.currentPage}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={
                    pagination.currentPage === pagination.totalPages || loading
                  }
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      )}

      {!loading && activeTab === 'waitlistByCity' && <WaitlistByCityPanel />}
    </div>
  );
}

export default UsersPage;
